import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, Users, Crown, Zap, Trophy, User } from "lucide-react";

interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  tier: string;
  subscription_status: string;
}

const TIER_BADGE: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; icon: typeof User }> = {
  free: { label: "Free", variant: "secondary", icon: User },
  light: { label: "Light", variant: "outline", icon: Zap },
  pro: { label: "Pro", variant: "default", icon: Crown },
  elite: { label: "Elite", variant: "default", icon: Trophy },
};

interface AdminUserTableProps {
  users: AdminUser[];
  loading: boolean;
  onRefresh: () => void;
}

export default function AdminUserTable({ users, loading, onRefresh }: AdminUserTableProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Registered Users ({users.length})
        </CardTitle>
        <Button variant="ghost" size="icon" onClick={onRefresh} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {loading && users.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground animate-pulse">Loading users...</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Last Sign In</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const tierInfo = TIER_BADGE[u.tier] || TIER_BADGE.free;
                  const TierIcon = tierInfo.icon;
                  return (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>
                        <Badge variant={tierInfo.variant} className="gap-1">
                          <TierIcon className="w-3 h-3" />
                          {tierInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`text-xs ${
                          u.subscription_status === "active" ? "text-green-500" :
                          u.subscription_status === "trialing" ? "text-blue-500" :
                          "text-muted-foreground"
                        }`}>
                          {u.subscription_status === "none" ? "Free" : u.subscription_status}
                        </span>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {u.last_sign_in_at
                          ? new Date(u.last_sign_in_at).toLocaleDateString()
                          : "Never"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
